import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { bulkUpdateUserPermissionsAPI } from "services/api/users/api";
import { PermissionWithAssignment } from "services/api/users/types";

import { useTranslate } from "helpers";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { CheckBox } from "ui/components/CheckBox";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import Tabs from "ui/components/Tabs/Tabs";
import { Tab } from "ui/components/Tabs/types";

import { UserPermissionBulkEditModalProps } from "./types";

const UserPermissionBulkEditModal: React.FC<UserPermissionBulkEditModalProps> = (props) => {
  const { show, userId, selectedPermissions, onClose, onSuccess } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [grantedPermissionIds, setGrantedPermissionIds] = useState<string[]>([]);
  const [deniedPermissionIds, setDeniedPermissionIds] = useState<string[]>([]);

  // Selektiraj sve checkboxove po defaultu kad se modal otvori
  useEffect(() => {
    if (show) {
      const unassignedIds = selectedPermissions.filter(p => !p.isAssigned).map(p => p.id);
      const assignedIds = selectedPermissions.filter(p => p.isAssigned).map(p => p.id);
      setGrantedPermissionIds(unassignedIds);
      setDeniedPermissionIds(assignedIds);
    }
  }, [show, selectedPermissions]);

  const handleToggleGrant = useCallback((permissionId: string) => {
    setGrantedPermissionIds(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }, []);

  const handleToggleDeny = useCallback((permissionId: string) => {
    setDeniedPermissionIds(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);

    try {
      await bulkUpdateUserPermissionsAPI(userId, {
        grantedPermissionIds,
        deniedPermissionIds,
      });

      onSuccess();
    } catch (error) {
      console.error("Error updating permissions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, grantedPermissionIds, deniedPermissionIds, onSuccess]);

  const unassignedPermissions = selectedPermissions.filter(p => !p.isAssigned);
  const assignedPermissions = selectedPermissions.filter(p => p.isAssigned);

  const hasChanges = grantedPermissionIds.length > 0 || deniedPermissionIds.length > 0;

  const addTabContent = (
    <div className="space-y-1.5 max-h-full overflow-y-auto p-3">
      {unassignedPermissions.length === 0 ? (
        <NoResults message={t("admin.user.permission.bulkedit.noadd")} />
      ) : (
        unassignedPermissions.map((permission: PermissionWithAssignment) => (
          <div
            key={permission.id}
            className="flex items-center justify-center p-3 hover:bg-green-50 rounded-md border border-slate-200"
          >
            <div className="pt-2">
              <CheckBox
                checked={grantedPermissionIds.includes(permission.id)}
                onChange={() => handleToggleGrant(permission.id)}
              />
            </div>
           <div className="ml-3 flex flex-1 items-center justify-between gap-4 text-sm font-medium ">
              <div>
                <div className="text-md">{permission.name}</div>
                {permission.description && (
                  <div className="text-sm font-medium text-gray-900">{permission.description}</div>
                )}
              </div>
              <div className="text-md">{permission.category}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const removeTabContent = (
    <div className="space-y-1.5 max-h-full overflow-y-auto p-3">
      {assignedPermissions.length === 0 ? (
        <NoResults message={t("admin.user.permission.bulkedit.noremove")} />
      ) : (
        assignedPermissions.map((permission: PermissionWithAssignment) => (
          <div
            key={permission.id}
            className="flex items-center justify-center p-3 hover:bg-red-50 rounded-sm border border-slate-200"
          >
            <div className="">
              <CheckBox
                checked={deniedPermissionIds.includes(permission.id)}
                onChange={() => handleToggleDeny(permission.id)}
              />
            </div>
            <div className="ml-3 flex flex-1 items-center justify-between gap-4 text-sm font-medium ">
              <div>
                <div className="text-md">{permission.name}</div>
                <div className="text-sm font-light text-slate-500">{permission.code}</div>
                {permission.description && (
                  <div className="text-sm font-medium text-gray-900">{permission.description}</div>
                )}
              </div>
              <div className="text-md">{permission.category}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const tabs: Tab[] = useMemo(() => [
    {
      label: `${t("admin.user.permission.bulkedit.add")} (${unassignedPermissions.length})`,
      content: addTabContent,
    },
    {
      label: `${t("admin.user.permission.bulkedit.remove")} (${assignedPermissions.length})`,
      content: removeTabContent,
    },
  ], [unassignedPermissions, assignedPermissions, grantedPermissionIds, deniedPermissionIds, t]);

  return (
    <Modal show={show} onHide={onClose} size="medium">
      <ModalHeader
        title={t("admin.user.permission.bulkedit.title")}
        onHide={onClose}
      />
      <ModalBody>
        <div className="pb-20 pt-3">
          <Tabs tabs={tabs} />
        </div>

        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            {t("admin.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!hasChanges || isLoading}
          >
            {t("admin.save")}
          </Button>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};

export default memo(UserPermissionBulkEditModal);
